// The SSE client (proposal 6.5) and the only EventSource in the SPA (RC1). One stream per team.
// Events carry ids; the browser's own reconnect sends Last-Event-ID, and when the browser gives up
// this client reopens with `after=<last id>`, so a dropped connection loses nothing.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { applyStreamEvent, invalidateAfterGap } from "./event-handlers";
import { STREAM_EVENT_NAMES, parseStreamEvent, type StreamEvent } from "./stream-event";

export type StreamStatus = "connecting" | "live" | "reconnecting";

export interface EventStreamOptions {
  teamId: string;
  onEvent: (event: StreamEvent) => void;
  /** `replayed` is true when the server was asked to replay from a known id. */
  onOpen?: (info: { replayed: boolean }) => void;
  onStatus?: (status: StreamStatus) => void;
  /** Tests pass a fake; the app uses the browser's EventSource. */
  createEventSource?: (url: string) => EventSource;
  random?: () => number;
}

const CLOSED = 2; // EventSource.CLOSED: the browser will not reconnect by itself.
const MAX_BACKOFF_MS = 30_000;

export function streamUrl(teamId: string, after: string | undefined): string {
  const params = new URLSearchParams({ team_id: teamId });
  if (after !== undefined) params.set("after", after);
  return `/api/events?${params.toString()}`;
}

export function openEventStream(options: EventStreamOptions): { close: () => void } {
  const create = options.createEventSource ?? ((url: string) => new EventSource(url));
  const random = options.random ?? Math.random;
  let lastEventId: string | undefined;
  let source: EventSource | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;
  let closed = false;
  // A closed stream in a hidden tab waits for the tab to show instead of retrying: a session that
  // ended answers 401 to every retry, and a hidden tab needs no live updates.
  let waitingForVisible = false;

  const connect = (): void => {
    retryTimer = undefined;
    const es = create(streamUrl(options.teamId, lastEventId));
    source = es;
    es.addEventListener("open", () => {
      failures = 0;
      options.onStatus?.("live");
      options.onOpen?.({ replayed: lastEventId !== undefined });
    });
    for (const name of STREAM_EVENT_NAMES) {
      es.addEventListener(name, (message) => {
        const frame = message as MessageEvent<string>;
        // The id advances even for a frame this client cannot read, as the browser's does.
        if (frame.lastEventId !== "") lastEventId = frame.lastEventId;
        const event = parseStreamEvent(name, frame.data, frame.lastEventId);
        if (event === undefined) {
          console.warn(`Dropped a malformed ${name} event from the stream`);
          return;
        }
        options.onEvent(event);
      });
    }
    es.addEventListener("error", () => {
      if (closed) return;
      options.onStatus?.("reconnecting");
      // While CONNECTING the browser retries by itself with Last-Event-ID. CLOSED means it gave up
      // (a non-200 answer), so this client reopens after a backoff with `after`.
      if (es.readyState === CLOSED) scheduleReconnect();
    });
  };

  const scheduleReconnect = (): void => {
    if (retryTimer !== undefined || waitingForVisible) return;
    source?.close();
    source = undefined;
    if (document.visibilityState === "hidden") {
      waitingForVisible = true;
      return;
    }
    const base = Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** failures);
    failures += 1;
    retryTimer = setTimeout(connect, base / 2 + (random() * base) / 2);
  };

  const onOnline = (): void => {
    if (retryTimer === undefined) return;
    clearTimeout(retryTimer);
    connect();
  };

  const onVisibilityChange = (): void => {
    if (!waitingForVisible || document.visibilityState !== "visible") return;
    waitingForVisible = false;
    connect();
  };

  options.onStatus?.("connecting");
  connect();
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    close: () => {
      closed = true;
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      source?.close();
    },
  };
}

/** Keeps one stream open for the team while mounted and returns its status. */
export function useTeamEvents(teamId: string): StreamStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>("connecting");
  useEffect(() => {
    const stream = openEventStream({
      teamId,
      onStatus: setStatus,
      // A fresh stream replays nothing, so anything that changed before it opened is refetched.
      onOpen: ({ replayed }) => {
        if (!replayed) void invalidateAfterGap(queryClient, teamId);
      },
      onEvent: (event) => applyStreamEvent(queryClient, teamId, event),
    });
    return () => stream.close();
  }, [queryClient, teamId]);
  return status;
}
